import { useState, useEffect, useRef } from 'react';
import { fetchWithCache } from '../../utils/apiCache';

export function useTableState({ targetTable, token, viewKey }) {
    const effectiveKey = viewKey || targetTable;

    const [sortConfig, setSortConfig] = useState([]); // [{ key: 'col', direction: 'asc'|'desc' }]
    const [distinctMode, setDistinctMode] = useState(false);
    const [preGroupHiddenColumns, setPreGroupHiddenColumns] = useState(null);
    const [injectedColumns, setInjectedColumns] = useState([]); // ['Core.Users.DepartmentID->Name']
    const [activeSubgrids, setActiveSubgrids] = useState([]); // ['Schema.Table.FKCol']
    const [hiddenColumns, setHiddenColumns] = useState([]); // ['colName']
    const [columnOrder, setColumnOrder] = useState([]); // ['col1', 'col2']
    const [columnWidths, setColumnWidths] = useState({}); // { colName: 200 }
    const [customColumnNames, setCustomColumnNames] = useState({}); // { colName: 'Custom Name' }
    const [columnFilters, setColumnFilters] = useState([]); // [{ column: 'colName', value: 'search' }]
    const [groupByConfig, setGroupByConfig] = useState([]); // ['colName']
    const [headerVisibility, setHeaderVisibility] = useState('auto'); // 'auto', 'show', 'hide'
    const [cascadeHideHeaders, setCascadeHideHeaders] = useState(false);
    
    const [searchQuery, setSearchQuery] = useState('');
    const [limit, setLimit] = useState(20);
    const [offset, setOffset] = useState(0);
    const [preferencesLoaded, setPreferencesLoaded] = useState(false);
    
    const [draggingCol, setDraggingCol] = useState(null);
    const [dragOverCol, setDragOverCol] = useState(null);
    
    const [currentKey, setCurrentKey] = useState(effectiveKey);
    if (effectiveKey !== currentKey) {
        setCurrentKey(effectiveKey);
        setSortConfig([]);
        setDistinctMode(false);
        setPreGroupHiddenColumns(null);
        setSearchQuery('');
        setLimit(20);
        setOffset(0);
        setInjectedColumns([]);
        setActiveSubgrids([]);
        setHiddenColumns([]);
        setColumnOrder([]);
        setColumnWidths({});
        setCustomColumnNames({});
        setColumnFilters([]);
        setGroupByConfig([]);
        setHeaderVisibility('auto');
        setCascadeHideHeaders(false);
        setPreferencesLoaded(false);
    }

    const debounceTimeout = useRef(null);

    useEffect(() => {
        if (!effectiveKey || !token) return;
        
        // Load preferences from database
        const loadPreferences = async () => {
            try {
                const key = `TableState_${effectiveKey}`;
                const res = await fetchWithCache(`/api/preferences/${key}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const prefs = await res.json();
                    if (prefs && prefs.value) {
                        const parsed = JSON.parse(prefs.value);
                        if (Array.isArray(parsed.sortConfig)) setSortConfig(parsed.sortConfig);
                        if (typeof parsed.distinctMode === 'boolean') setDistinctMode(parsed.distinctMode);
                        if (parsed.preGroupHiddenColumns !== undefined) setPreGroupHiddenColumns(parsed.preGroupHiddenColumns);
                        if (Array.isArray(parsed.injectedColumns)) setInjectedColumns(parsed.injectedColumns);
                        if (Array.isArray(parsed.activeSubgrids)) setActiveSubgrids(parsed.activeSubgrids);
                        if (Array.isArray(parsed.hiddenColumns)) setHiddenColumns(parsed.hiddenColumns);
                        if (Array.isArray(parsed.columnOrder)) setColumnOrder(parsed.columnOrder);
                        if (parsed.columnWidths && typeof parsed.columnWidths === 'object') setColumnWidths(parsed.columnWidths);
                        if (parsed.customColumnNames && typeof parsed.customColumnNames === 'object') setCustomColumnNames(parsed.customColumnNames);
                        if (Array.isArray(parsed.columnFilters)) setColumnFilters(parsed.columnFilters);
                        if (Array.isArray(parsed.groupByConfig)) setGroupByConfig(parsed.groupByConfig);
                        if (parsed.headerVisibility) setHeaderVisibility(parsed.headerVisibility);
                        if (typeof parsed.cascadeHideHeaders === 'boolean') setCascadeHideHeaders(parsed.cascadeHideHeaders);
                    }
                }
            } catch (err) {
                console.error('Failed to load preferences:', err);
            } finally {
                setPreferencesLoaded(true);
            }
        };

        setPreferencesLoaded(false);
        
        loadPreferences();
    }, [effectiveKey, token]);

    // Save preferences when they change
    useEffect(() => {
        if (!effectiveKey || !token || !preferencesLoaded) return;

        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

        debounceTimeout.current = setTimeout(async () => {
            try {
                const key = `TableState_${effectiveKey}`;
                const payload = JSON.stringify({ sortConfig, distinctMode, preGroupHiddenColumns, injectedColumns, activeSubgrids, hiddenColumns, columnOrder, columnWidths, customColumnNames, columnFilters, groupByConfig, headerVisibility, cascadeHideHeaders });
                await fetch(`/api/preferences/${key}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ value: payload })
                });
            } catch (err) {
                console.error('Failed to save preferences:', err);
            }
        }, 1000); // 1s debounce

        return () => clearTimeout(debounceTimeout.current);
    }, [sortConfig, distinctMode, preGroupHiddenColumns, injectedColumns, activeSubgrids, hiddenColumns, columnOrder, columnWidths, customColumnNames, columnFilters, groupByConfig, headerVisibility, cascadeHideHeaders, effectiveKey, token, preferencesLoaded]);

    const handleSort = (columnName) => {
        setSortConfig(prev => {
            const existing = prev.find(s => s.key === columnName);
            if (!existing) return [...prev, { key: columnName, direction: 'asc' }];
            if (existing.direction === 'asc') return prev.map(s => s.key === columnName ? { ...s, direction: 'desc' } : s);
            return prev.filter(s => s.key !== columnName);
        });
    };

    const toggleInjectedColumn = (colPath) => {
        setInjectedColumns(prev => {
            if (prev.includes(colPath)) return prev.filter(c => c !== colPath);
            return [...prev, colPath];
        });
    };

    const toggleGroupBy = (colName) => {
        setGroupByConfig(prev => {
            if (prev.includes(colName)) return prev.filter(c => c !== colName);
            return [...prev, colName];
        });
    };

    const moveGroup = (fromIdx, toIdx) => {
        setGroupByConfig(prev => {
            const next = [...prev];
            const [moved] = next.splice(fromIdx, 1);
            next.splice(toIdx, 0, moved);
            return next;
        });
    };

    const removeGroup = (colName) => {
        setGroupByConfig(prev => prev.filter(c => c !== colName));
    };

    const groupOnColumn = (columnName, allColumns) => {
        // "grouping on a column is supposed to simply create a table view with just that one column, 
        // and add a self-subgrid that filters by each paren't row's grouping."
        if (!distinctMode) {
            setPreGroupHiddenColumns(hiddenColumns);
            setDistinctMode(true);
        }
        
        // Hide all columns EXCEPT the selected one
        const newHidden = allColumns.map(c => c.ColumnName).filter(n => n !== columnName);
        setHiddenColumns(newHidden);

        // Add the self-subgrid
        const selfSubgrid = `${targetTable}:SELF`;
        setActiveSubgrids(prev => {
            if (!prev.includes(selfSubgrid)) return [...prev, selfSubgrid];
            return prev;
        });
    };

    const deactivateGroupMode = (childStates = {}) => {
        // Find if any of our active children is a SELF subgrid in distinct mode
        let mergedChild = null;
        Object.keys(childStates).forEach(key => {
            if (key.includes(`_subgrid_${targetTable}_${targetTable}:SELF_`) || key.includes(`${targetTable}:SELF`)) {
                if (childStates[key].distinctMode) {
                    mergedChild = childStates[key];
                }
            }
        });

        if (mergedChild) {
            // MERGE MODE: The child is grouped. 
            // We absorb its visible columns into ours, keep distinctMode = true, and close the subgrid.
            const childVisible = mergedChild.visibleColumns || [];
            // Unhide whatever the child was showing
            setHiddenColumns(prev => {
                const newHidden = { ...prev };
                Object.keys(newHidden).forEach(col => {
                    // If the child is NOT hiding this column, we should NOT hide it either
                    if (childVisible[col] === false) {
                        newHidden[col] = false;
                    }
                });
                return newHidden;
            });
            // Just close the self subgrid, keeping distinctMode true
            const selfSubgrid = `${targetTable}:SELF`;
            setActiveSubgrids(prev => prev.filter(sg => sg !== selfSubgrid));
            return;
        }

        // STANDARD UNGROUP MODE: No child is grouped. Restore original columns.
        setDistinctMode(false);
        if (preGroupHiddenColumns !== null) {
            setHiddenColumns(preGroupHiddenColumns);
            setPreGroupHiddenColumns(null);
        }
        
        const selfSubgrid = `${targetTable}:SELF`;
        setActiveSubgrids(prev => prev.filter(sg => sg !== selfSubgrid));
    };

    return {
        sortConfig,
        setSortConfig,
        distinctMode,
        setDistinctMode,
        preGroupHiddenColumns,
        setPreGroupHiddenColumns,
        injectedColumns,
        setInjectedColumns,
        activeSubgrids,
        setActiveSubgrids,
        hiddenColumns,
        setHiddenColumns,
        columnOrder,
        setColumnOrder,
        columnWidths,
        setColumnWidths,
        customColumnNames,
        setCustomColumnNames,
        columnFilters,
        setColumnFilters,
        searchQuery,
        setSearchQuery,
        limit,
        setLimit,
        offset,
        setOffset,
        headerVisibility,
        setHeaderVisibility,
        cascadeHideHeaders,
        setCascadeHideHeaders,
        groupByConfig,
        setGroupByConfig,
        toggleGroupBy,
        moveGroup,
        removeGroup,
        handleSort,
        draggingCol,
        setDraggingCol,
        dragOverCol,
        setDragOverCol,
        headerVisibility,
        toggleInjectedColumn,
        groupOnColumn,
        deactivateGroupMode,
        preferencesLoaded
    };
}
